import { toTitleCase, getSemesterDifference, getAbsoluteSemester } from '../utils';
export function findModules(blockId, blocks) {
  const block = blocks.find(b => b.id === blockId);
  if (!block) return [];

  let modules = block.modules ? [...block.modules] : [];

  if (block.blocks && block.blocks.length > 0) {
    modules = modules.concat(block.blocks.flatMap(innerBlockId => findModules(innerBlockId, blocks)));
  }

  return modules;
}

export function filterModulesByInput(modules, moduleInput) {
  if (!moduleInput) return modules;
  const searchTerm = moduleInput.toLowerCase();
  return modules.filter(module => {
    return (
      module.name.toLowerCase().includes(searchTerm) ||
      (module.number && module.number.toString().toLowerCase().includes(searchTerm))
    );
  });
}

export function prioritiesOfStructureElements(program, preferences) {
  /* prioritze structure elements. For a given module, the module is displayed
     in the (possibly multiple) structure groups that has the highest priority. 
     
  */
  const priorities = new Map();
  for (let structure of program.structures) {
    for (let element of structure.elements) {
      if (element?.priority) {
        priorities.set(element.id, element.priority);
      } else if (structure.exclusive) {
        if (preferences.structures[structure.id] === element.id) {
          priorities.set(element.id, 1);
        } else {
          priorities.set(element.id, 1);
        }
      } else {
        priorities.set(element.id, 1);
      }
    }
  }
  return priorities;
}

export function modulesWithStructures(program, preferences, priorities = null) {
  const resolvedPriorities = priorities || prioritiesOfStructureElements(program, preferences);
  const moduleStructuresMap = new Map();

  const processElement = (element, structure) => {
    if (!element || !element.blocks) return;

    const elementPriority = resolvedPriorities.get(element.id) ?? -99;

    element.blocks.forEach(block => {
      let modules = block.modules ? [...block.modules] : [];
      if (block.blocks && block.blocks.length > 0) {
        modules = modules.concat(block.blocks.flatMap(blockId => findModules(blockId, program.blocks)));
      }

      modules.forEach(moduleId => {
        const currentData = moduleStructuresMap.get(moduleId);
        const currentPriority = currentData ? currentData.priority : -99;
        const newStructureInfo = {
          elementId: element.id,
          structureId: structure.id,
          structureColor: structure.highlight,
        };

        if (elementPriority > currentPriority) {
          // Higher priority found, override existing structures
          moduleStructuresMap.set(moduleId, {
            priority: elementPriority,
            structures: [newStructureInfo],
          });
        } else if (elementPriority === currentPriority) {
          // Equal priority, append to list of structures
          if (currentData) {
            // Ensure no duplicate structure-element mapping
            const exists = currentData.structures.some(
              s => s.elementId === element.id && s.structureId === structure.id
            );
            if (!exists) {
              currentData.structures.push(newStructureInfo);
            }
          } else {
            moduleStructuresMap.set(moduleId, {
              priority: elementPriority,
              structures: [newStructureInfo],
            });
          }
        }
      });
    });
  };

  const processBlock = (block, priority, groupBlockId) => {
    if (!block) return;

    if (block.modules && block.modules.length > 0) {
      block.modules.forEach(moduleId => {
        const currentData = moduleStructuresMap.get(moduleId);
        const currentPriority = currentData ? currentData.priority : -99;
        const newStructureInfo = {
          elementId: block.id,
          structureId: groupBlockId,
          structureColor: null,
        };

        if (priority > currentPriority) {
          // Higher priority found, override existing structures
          moduleStructuresMap.set(moduleId, {
            priority: priority,
            structures: [newStructureInfo],
          });
        } else if (priority === currentPriority) {
          // Equal priority, append to list of structures
          if (currentData) {
            // Ensure no duplicate structure-element mapping
            const exists = currentData.structures.some(
              s => s.elementId === block.id && s.structureId === groupBlockId
            );
            if (!exists) {
              currentData.structures.push(newStructureInfo);
            }
          } else {
            moduleStructuresMap.set(moduleId, {
              priority: priority,
              structures: [newStructureInfo],
            });
          }
        }
      });
    }

    if (block.blocks && block.blocks.length > 0) {
      block.blocks.forEach(subBlockId => {
        const subBlock = program.blocks.find(b => b.id === subBlockId);
        if (subBlock) {
          const subPriority = subBlock.dropdownData?.priority ?? priority;
          processBlock(subBlock, subPriority, groupBlockId);
        }
      });
    }
  };

  program.structures.forEach(structure => {
    structure.elements.forEach(element => {
      processElement(element, structure);
    });
  });

  // Find all parent dropdown blocks and their subblock IDs
  const parentDropdownBlocks = [];
  const dropdownSubBlockIds = new Set();

  program.blocks.forEach(block => {
    if (block.showInDropdown) {
      if (block.blocks && block.blocks.length > 0) {
        parentDropdownBlocks.push(block);
        block.blocks.forEach(id => dropdownSubBlockIds.add(id));
      }
    }
  });

  // Process parent dropdown blocks' subblocks
  parentDropdownBlocks.forEach(parentBlock => {
    parentBlock.blocks.forEach(subBlockId => {
      const subBlock = program.blocks.find(b => b.id === subBlockId);
      if (subBlock) {
        const priority = subBlock.dropdownData?.priority ?? parentBlock.dropdownData?.priority ?? -99;
        processBlock(subBlock, priority, parentBlock.id);
      }
    });
  });

  // Process flat dropdown blocks
  program.blocks.forEach(block => {
    if (block.showInDropdown && !dropdownSubBlockIds.has(block.id) && (!block.blocks || block.blocks.length === 0)) {
      const priority = block.dropdownData?.priority ?? -99;
      processBlock(block, priority, block.id);
    }
  });

  return Array.from(moduleStructuresMap.entries()).map(([moduleId, data]) => ({
    moduleId,
    priority: data.priority,
    structures: data.structures,
  }));
}

export function isNoneStructureElement(element) {
  if (!element) return false;
  if (element.is_none === true || element.isNone === true || element.no_modules === true || element.hide_in_dropdown === true || element.hideInDropdown === true) {
    return true;
  }
  if (['no_minor', 'no_specialization', 'no_sup', 'no_sup2'].includes(element.id)) {
    return true;
  }
  return false;
}

export function selectedStructures(program, preferences) {
  let selectedStructuresList = [];
  program.structures.forEach(structure => {
    const selectedElementId = preferences.structures[structure.id];
    if (selectedElementId) {
      const selectedElement = structure.elements.find(element => element.id === selectedElementId);
      if (selectedElement && !isNoneStructureElement(selectedElement)) {
        selectedStructuresList.push(selectedElement);
      }
    }
  });
  return selectedStructuresList;
}

export function getSelectableModules(program, preferences, {
  markedModules = [],
  hiddenModules = [],
  oneModulePerMockName = false,
  mockModules = [],
  excludeDepartmentMaMock = false,
  moduleInput = ""
} = {}) {
  let uniqueMockModules = new Set();
  const markedModuleNames = new Set(
    markedModules.map(id => {
      const foundModule = program.modules.find(module => module.id === id);
      return foundModule ? foundModule.name : null;
    })
  );

  let programModules = program.blocks.flatMap(block => block.modules || []);
  program.structures.forEach(structure => {
    if (structure.exclusive) {
      // Handle exclusive structure: Only include modules from the selected element
      const selectedElementId = preferences.structures[structure.id];
      const selectedElement = structure.elements.find(element => element.id === selectedElementId);
      if (selectedElement) {
        selectedElement.blocks.forEach(block => {
          programModules = [...programModules, ...(block.modules || [])];
        });
      }
    } else {
      // Handle non-exclusive structure: Include modules from all elements
      structure.elements.forEach(element => {
        element.blocks.forEach(block => {
          programModules = [...programModules, ...(block.modules || [])];
        });
      });
    }
  });

  // First filter modules based on all criteria except search
  let filteredModules = program.modules.filter(module => {
    // Exclude hidden and marked modules
    if (hiddenModules.includes(module.id) || markedModules.includes(module.id)) {
      return false;
    }
    // Exclude modules that are not part of program with selected structures
    if (!programModules.includes(module.id)) {
      return false;
    }
    // Exclude departmentMa mock modules (Special topics in ...)
    if (excludeDepartmentMaMock && mockModules.includes(module.id) && module.departmentMa === true) {
      return false;
    }
    // Handle mock modules if only one per name can be added
    if (module.mock && oneModulePerMockName && markedModuleNames.has(module.name)) {
      return false;
    }
    // Handle mock modules if more than one per name can be added
    if (module.mock) {
      const uniqueKey = oneModulePerMockName ? module.name : `${module.name}_${module.ects}`;
      if (uniqueMockModules.has(uniqueKey)) {
        return false;
      }
      uniqueMockModules.add(uniqueKey);
    }
    return true;
  });

  if (moduleInput) {
    filteredModules = filterModulesByInput(filteredModules, moduleInput);
  }

  // Finally sort the results
  return filteredModules.sort((a, b) => {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
}

function buildBlockGroupTree(block, programBlocks, isRoot = false) {
  const structure = {
    id: block.id,
    name: block.dropdownData?.name || block.name,
    dropdownData: block.dropdownData,
    isCustomName: true,
    isBlock: true
  };

  const node = {
    structure,
    modules: [],
    subgroups: []
  };

  if (isRoot) {
    structure.isBlock = true;
  }

  if (block.blocks && block.blocks.length > 0) {
    node.isParent = true;
    block.blocks.forEach(subBlockId => {
      const subBlock = programBlocks.find(b => b.id === subBlockId);
      if (subBlock) {
        node.subgroups.push(buildBlockGroupTree(subBlock, programBlocks, false));
      }
    });
  }

  return node;
}

function findNodeInTree(node, targetId) {
  if (node.structure && node.structure.id === targetId) {
    return node;
  }
  if (node.subgroups && node.subgroups.length > 0) {
    for (let subgroup of node.subgroups) {
      const found = findNodeInTree(subgroup, targetId);
      if (found) return found;
    }
  }
  return null;
}

function isRegularlyOffered(module, program) {
  const occ = module?.occurrence || (program?.moduleOccurrences ? program.moduleOccurrences[module.id] : null);
  const normOcc = typeof occ === 'string' ? occ.toLowerCase() : occ;
  return ['w', 's', 'w/s', 's/w'].includes(normOcc);
}

function cleanAndSortGroupTree(node) {
  if (node.subgroups && node.subgroups.length > 0) {
    node.subgroups.forEach(sg => cleanAndSortGroupTree(sg));
    node.subgroups = node.subgroups.filter(sg => (sg.modules && sg.modules.length > 0) || (sg.subgroups && sg.subgroups.length > 0));
    node.subgroups.sort((a, b) => {
      const nameA = a.structure?.name || '';
      const nameB = b.structure?.name || '';
      return nameA.localeCompare(nameB);
    });
  }
  if (node.modules && node.modules.length > 0) {
    node.modules.sort((a, b) => {
      const unofferedA = !!a.isUnavailable;
      const unofferedB = !!b.isUnavailable;
      if (unofferedA !== unofferedB) {
        return unofferedA ? 1 : -1;
      }
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
    });
  }
}

function getRequiredModuleIds(program, preferences) {
  if (!program) return [];

  const required = (program.blocks || []).filter(block => block.allRequired)
    .map(block => block.modules || [])
    .flat();

  let strucRequired = [];

  (program.structures || []).forEach(struc => {
    const selectedElementId = preferences?.structures?.[struc.id];
    const selectedElement = struc.elements?.find(element => element.id === selectedElementId);

    if (selectedElement) {
      (selectedElement.blocks || []).forEach(block => {
        if (block.allRequired) {
          strucRequired = [...strucRequired, ...(block.modules || [])];
        }
      });
    }
  });
  return [...new Set([...required, ...strucRequired])];
}

export function getReferenceSemester(preferences, targetSemester = null) {
  let targetAbs = null;
  if (targetSemester !== null && targetSemester !== undefined) {
    if (typeof targetSemester === 'number' || /^\d+$/.test(targetSemester)) {
      if (preferences?.startSemester) {
        try {
          targetAbs = getAbsoluteSemester(parseInt(targetSemester, 10), preferences.startSemester);
        } catch (e) {
          // ignore
        }
      }
    } else if (typeof targetSemester === 'string' && /^[sw]\d{2}$/.test(targetSemester)) {
      targetAbs = targetSemester;
    }
  }

  const currentSem = preferences?.currentSemester || preferences?.startSemester || null;

  if (targetAbs && currentSem) {
    try {
      return getSemesterDifference(targetAbs, currentSem) > 0 ? targetAbs : currentSem;
    } catch (e) {
      return targetAbs || currentSem;
    }
  }

  return targetAbs || currentSem;
}

export function isModuleValidToInPast(module, referenceSemester) {
  const validTo = module?.validTo || module?.valid_to;
  if (!validTo || !referenceSemester) return false;
  const pattern = /^[sw]\d{2}$/;
  if (!pattern.test(validTo) || !pattern.test(referenceSemester)) return false;
  try {
    return getSemesterDifference(referenceSemester, validTo) > 0;
  } catch (e) {
    return false;
  }
}

export function getGroupedModules(program, preferences, {
  selectableAvailableModules = [],
  selectableUnavailableModules = [],
  modulesWithStructures = [],
  extraGroup = null,
  targetSemester = null
} = {}) {
  const referenceSemester = getReferenceSemester(preferences, targetSemester);

  const regularUnavailableModules = [];
  const irregularUnavailableModules = [];

  selectableUnavailableModules.forEach(module => {
    const item = { ...module, isUnavailable: true };
    if (isRegularlyOffered(module, program) && !isModuleValidToInPast(module, referenceSemester)) {
      regularUnavailableModules.push(item);
    } else {
      irregularUnavailableModules.push(item);
    }
  });

  const filteredAvailableModules = [];
  selectableAvailableModules.forEach(module => {
    if (isModuleValidToInPast(module, referenceSemester)) {
      if (!irregularUnavailableModules.some(m => m.id === module.id)) {
        irregularUnavailableModules.push({ ...module, isUnavailable: true });
      }
    } else {
      filteredAvailableModules.push(module);
    }
  });

  const shouldGroupRequired = program.groupRequiredModules !== false;
  const requiredModuleIds = shouldGroupRequired ? getRequiredModuleIds(program, preferences) : [];

  let availableRequiredModules = [];
  let requiredUnavailableModules = [];
  if (shouldGroupRequired) {
    availableRequiredModules = filteredAvailableModules.filter(module =>
      requiredModuleIds.includes(module.id)
    );
    requiredUnavailableModules = regularUnavailableModules.filter(module =>
      requiredModuleIds.includes(module.id)
    );
  }
  const requiredGroupModules = [...availableRequiredModules, ...requiredUnavailableModules];
  const otherSelectableAvailableModules = filteredAvailableModules;

  // 1. If program has no structures and no custom dropdown blocks, return all modules in a single default group
  const hasDropdownBlocks = program.blocks && program.blocks.some(block => block.showInDropdown);
  if (program.structures.length === 0 && !hasDropdownBlocks) {
    const defaultGroups = [];
    if (shouldGroupRequired && requiredGroupModules.length > 0) {
      defaultGroups.push({
        structure: {
          id: "required-modules",
          name: "Required Modules",
          isCustomName: true,
          isRequiredGroup: true
        },
        modules: requiredGroupModules
      });
    }
    if (filteredAvailableModules.length > 0) {
      defaultGroups.push({
        structure: {
          id: "no-structure",
          name: "All available modules",
          isCustomName: true
        },
        modules: [...filteredAvailableModules]
      });
    }
    if (irregularUnavailableModules.length > 0) {
      defaultGroups.push({
        isUnavailable: true,
        structure: {
          id: "unavailable"
        },
        modules: irregularUnavailableModules
      });
    }
    cleanAndSortGroupTree({ subgroups: defaultGroups });
    return defaultGroups;
  }

  const groups = [];
  const structureMap = new Map();

  if (shouldGroupRequired && requiredGroupModules.length > 0) {
    groups.push({
      structure: {
        id: "required-modules",
        name: "Required Modules",
        isCustomName: true,
        isRequiredGroup: true
      },
      modules: requiredGroupModules
    });
  }

  // Create unavailable modules group first (for irregular unavailable modules)
  if (irregularUnavailableModules.length > 0) {
    const unavailGroup = {
      isUnavailable: true,
      structure: {
        id: "unavailable"
      },
      modules: irregularUnavailableModules
    };
    cleanAndSortGroupTree(unavailGroup);
    groups.push(unavailGroup);
  }

  // 2. Initialize structure groups in the map
  // Collect all selected element names across all structures to exclude them from any "other" groups
  const allSelectedElementNames = new Set();
  program.structures.forEach(struc => {
    const selId = preferences?.structures?.[struc.id];
    if (selId) {
      const selEl = struc.elements?.find(e => e.id === selId);
      if (selEl && selEl.name && !isNoneStructureElement(selEl)) {
        allSelectedElementNames.add(selEl.name.toLowerCase().trim());
      }
    }
  });

  // First pass: create groups for structures that are NOT grouped into others
  // Second pass: merge grouped structures' "other" elements into the target's "other" group
  const groupedOthersMap = new Map(); // maps structure.id -> target structure.id for "other" merging

  program.structures.forEach(structure => {
    const dropdownGroupOthersWith = structure.dropdownGroupOthersWith !== undefined
      ? structure.dropdownGroupOthersWith
      : structure.dropdown_group_others_with;
    if (dropdownGroupOthersWith) {
      groupedOthersMap.set(structure.id, dropdownGroupOthersWith);
    }
  });

  program.structures.forEach(structure => {
    const selectedElementId = preferences.structures[structure.id];
    const selectedElement = structure.elements.find(e => e.id === selectedElementId);
    const hasValidSelectedElement = selectedElement && !isNoneStructureElement(selectedElement);

    if (!structure.exclusive) {
      // Group for the currently selected element
      if (hasValidSelectedElement) {
        structureMap.set(selectedElementId, {
          structure: {
            ...structure,
            id: selectedElementId,
            structureId: structure.id,
            isStructure: true,
            isYourStructure: true,
            name: `Modules of your ${structure.name.toLowerCase()} (${toTitleCase(selectedElement.name.toLowerCase())})`,
            isCustomName: true
          },
          modules: []
        });

      }

      const isShowInDropdown = structure.showInDropdown !== undefined ? structure.showInDropdown : structure.show_in_dropdown;
      const dropdownGroupOthersWith = structure.dropdownGroupOthersWith !== undefined
        ? structure.dropdownGroupOthersWith
        : structure.dropdown_group_others_with;

      if (dropdownGroupOthersWith) {
        // This structure's "other" elements will be merged into the target structure's "other" group.
        // We defer adding subgroups until after the target's group is created.
      } else if (isShowInDropdown !== false) {
        // Check if any other structure is grouping its "others" into this one
        const hasGroupedStructures = [...groupedOthersMap.values()].includes(structure.id);

        // Parent group for other elements in this structure
        const parentId = `other-${structure.id}`;
        const pluralize = str => str.endsWith('s') ? str : `${str}s`;
        let parentName;
        if (hasGroupedStructures) {
          // Combined name: strip trailing numbers/suffixes for a shared name
          // e.g. "Supplementary area 1" -> "Modules of other supplementary areas"
          const baseName = structure.name.replace(/\s*\d+\s*$/, '').toLowerCase();
          parentName = hasValidSelectedElement
            ? `Modules of other ${pluralize(baseName)}`
            : `Modules of ${pluralize(baseName)}`;
        } else {
          const structName = structure.name.toLowerCase();
          parentName = hasValidSelectedElement
            ? `Modules of other ${pluralize(structName)}`
            : `Modules of ${pluralize(structName)}`;
        }

        structureMap.set(parentId, {
          structure: {
            ...structure,
            id: parentId,
            structureId: structure.id,
            isStructure: true,
            isOtherStructure: true,
            name: parentName,
            isCustomName: true
          },
          isParent: true,
          subgroups: []
        });

        // Subgroups for other elements of this structure
        structure.elements.forEach(element => {
          const normName = element.name.toLowerCase().trim();
          if (element.id !== selectedElementId && !allSelectedElementNames.has(normName) && !isNoneStructureElement(element)) {
            structureMap.get(parentId).subgroups.push({
              structure: {
                ...structure,
                id: `element-${element.id}`,
                name: element.name,
                isCustomName: true
              },
              modules: []
            });
          }
        });
      }
    } else {
      // Group for exclusive structure
      if (hasValidSelectedElement) {
        structureMap.set(structure.id, {
          structure: {
            ...structure,
            structureId: structure.id,
            isStructure: true,
            isYourStructure: true,
            name: `Modules of your ${structure.name.toLowerCase()} (${toTitleCase(selectedElement.name.toLowerCase())})`,
            isCustomName: true
          },
          modules: []
        });
      }
    }
  });

  // Second pass: add "other" elements from grouped structures into the target's "other" group
  // Deduplicate by merging elements with the same name into existing subgroups
  const elementIdMapping = new Map(); // maps element ID -> canonical subgroup element ID (for module routing)
  groupedOthersMap.forEach((targetStructureId, sourceStructureId) => {
    const sourceStructure = program.structures.find(s => s.id === sourceStructureId);
    if (!sourceStructure) return;

    const selectedElementId = preferences.structures[sourceStructureId];
    const targetParentId = `other-${targetStructureId}`;
    const targetParentGroup = structureMap.get(targetParentId);
    if (!targetParentGroup) return;

    sourceStructure.elements.forEach(element => {
      const normName = element.name.toLowerCase().trim();
      if (element.id !== selectedElementId && !allSelectedElementNames.has(normName) && !isNoneStructureElement(element)) {
        // Check if a subgroup with the same name already exists (from the target structure)
        const existingSubgroup = targetParentGroup.subgroups.find(
          sg => sg.structure.name.toLowerCase().trim() === normName
        );
        if (existingSubgroup) {
          // Map this element's ID to the existing subgroup's element ID for module routing
          elementIdMapping.set(element.id, existingSubgroup.structure.id);
        } else {
          targetParentGroup.subgroups.push({
            structure: {
              ...sourceStructure,
              id: `element-${element.id}`,
              name: element.name,
              isCustomName: true
            },
            modules: []
          });
        }
      }
    });
  });

  // Find all subblock IDs of parent dropdown blocks
  const dropdownSubBlockIds = new Set();
  program.blocks.forEach(block => {
    if (block.showInDropdown && block.blocks && block.blocks.length > 0) {
      block.blocks.forEach(id => dropdownSubBlockIds.add(id));
    }
  });

  // Initialize block dropdown groups in the map
  program.blocks.forEach(block => {
    if (block.showInDropdown) {
      if (dropdownSubBlockIds.has(block.id)) {
        return;
      }
      const treeNode = buildBlockGroupTree(block, program.blocks, true);
      structureMap.set(block.id, treeNode);
    }
  });

  // 3. Set up the optional extra modules group if present
  if (extraGroup) {
    const extraId = extraGroup.id || 'extra';
    structureMap.set(extraId, {
      structure: {
        id: extraId,
        name: extraGroup.name || 'Extra Modules',
        isExtra: true
      },
      modules: []
    });
  }

  // 4. Distribute modules into their respective structure groups
  const modulesToDistribute = [
    ...otherSelectableAvailableModules,
    ...regularUnavailableModules
  ];

  modulesToDistribute.forEach(module => {
    // First check if the module belongs to the extra group
    if (extraGroup && extraGroup.moduleids?.includes(module.id)) {
      const extraId = extraGroup.id || 'extra';
      const group = structureMap.get(extraId);
      if (group) {
        if (!group.modules.some(m => m.id === module.id)) {
          group.modules.push(module);
        }
      }
      return;
    }

    // Find structures the module belongs to
    const moduleStructureData = modulesWithStructures.find(item => item.moduleId === module.id);

    if (moduleStructureData && moduleStructureData.structures && moduleStructureData.structures.length > 0) {
      moduleStructureData.structures.forEach(structEl => {
        const blockDef = program.blocks.find(b => b.id === structEl.structureId);
        if (blockDef && blockDef.showInDropdown) {
          const group = structureMap.get(blockDef.id);
          if (group) {
            const targetNode = findNodeInTree(group, structEl.elementId);
            if (targetNode) {
              if (!targetNode.modules.some(m => m.id === module.id)) {
                targetNode.modules.push(module);
              }
            }
          }
          return;
        }

        const structDef = program.structures.find(s => s.id === structEl.structureId);
        if (!structDef) return;

        if (structDef.exclusive) {
          // Add to exclusive structure group, but only for modules of the selected element
          if (structEl.elementId !== preferences.structures[structDef.id]) return;
          const group = structureMap.get(structDef.id);
          if (group && !group.modules.some(m => m.id === module.id)) {
            group.modules.push(module);
          }
        } else {
          // Add to non-exclusive structure group (either selected element or "other" subgroups)
          const selectedGroup = structureMap.get(structEl.elementId);
          if (selectedGroup) {
            if (!selectedGroup.modules.some(m => m.id === module.id)) {
              selectedGroup.modules.push(module);
            }
          } else {
            // Resolve the correct "other" parent: if this structure groups into another, use the target
            const resolvedParentStructureId = groupedOthersMap.get(structDef.id) || structDef.id;
            const parentId = `other-${resolvedParentStructureId}`;
            const parentGroup = structureMap.get(parentId);
            if (parentGroup) {
              // Resolve element ID through dedup mapping (e.g., sup2's "alg_sup2" -> sup1's "element-alg_sup")
              const resolvedElementId = elementIdMapping.get(structEl.elementId) || `element-${structEl.elementId}`;
              const subgroup = parentGroup.subgroups.find(sg => sg.structure.id === resolvedElementId);
              if (subgroup && !subgroup.modules.some(m => m.id === module.id)) {
                subgroup.modules.push(module);
              }
            }
          }
        }
      });
    }
  });

  // 5. Gather and filter non-empty groups
  structureMap.forEach(group => {
    cleanAndSortGroupTree(group);

    if (group.isParent) {
      if (group.subgroups.length > 0 || (group.modules && group.modules.length > 0)) {
        groups.push(group);
      }
    } else if (group.modules && group.modules.length > 0) {
      groups.push(group);
    }
  });

  // Add "All available modules" group with all selectable available modules
  if (filteredAvailableModules.length > 0) {
    const allAvailableGroup = {
      structure: {
        id: "no-structure",
        name: "All available modules",
        isCustomName: true
      },
      modules: [...filteredAvailableModules]
    };
    cleanAndSortGroupTree(allAvailableGroup);
    groups.push(allAvailableGroup);
  }

  groups.sort((a, b) => {
    // Helper to classify groups into categories
    const getCategory = (g) => {
      if (g.structure?.isRequiredGroup) return -1;
      if (g.structure?.isExtra) return 0;
      if (g.isUnavailable) return 99;
      if (!g.structure || g.structure.id === "no-structure") return 98;
      if (g.structure?.isStructure) return 1;
      if (g.structure?.isBlock) return 2;
      return 2;
    };

    const catA = getCategory(a);
    const catB = getCategory(b)

    if (catA !== catB) {
      return catA - catB;
    }

    // Within Categories 1 (Structures) and 2 (Blocks), sort by priority if set
    if (catA === 1 || catA === 2) {
      const priorityA = a.structure?.dropdownData?.priority ?? 0;
      const priorityB = b.structure?.dropdownData?.priority ?? 0;
      if (priorityA !== priorityB) {
        return priorityB - priorityA; // descending order
      }
    }

    // Tie-breaker if priorities are equal
    if (catA === 1) {
      // Category 1: Structure groups — sort all "your" groups before all "other" groups
      if (a.structure.isYourStructure && b.structure.isOtherStructure) return -1;
      if (b.structure.isYourStructure && a.structure.isOtherStructure) return 1;

      // Within the same type (both "your" or both "other"), sort by structure index
      const indexA = program.structures.findIndex(s => s.id === a.structure.structureId);
      const indexB = program.structures.findIndex(s => s.id === b.structure.structureId);
      if (indexA !== -1 && indexB !== -1) {
        if (indexA !== indexB) {
          return indexA - indexB;
        }
      } else {
        const nameA = a.structure.name || '';
        const nameB = b.structure.name || '';
        const cmp = nameA.localeCompare(nameB);
        if (cmp !== 0) return cmp;
      }

      return 0;
    }

    if (catA === 2) {
      // Category 2: Block groups
      const indexA = program.blocks.findIndex(block => block.id === a.structure.id);
      const indexB = program.blocks.findIndex(block => block.id === b.structure.id);
      if (indexA !== -1 && indexB !== -1 && indexA !== indexB) {
        return indexA - indexB;
      }
      const nameA = a.structure?.name || '';
      const nameB = b.structure?.name || '';
      return nameA.localeCompare(nameB);
    }

    return 0;
  });

  return groups;
}

export function getStructureColorClass(moduleId, modulesWithStructuresList) {
  const moduleStructure = modulesWithStructuresList.find(item => item.moduleId === moduleId);
  if (moduleStructure && moduleStructure.structures && moduleStructure.structures.length > 0) {
    const firstStruct = moduleStructure.structures[0];
    let color = firstStruct.structureColor;
    if (firstStruct.structureId === "track") {
      color = `${color}_light`;
    }
    return `structure-color-${color}`;
  }
  return '';
}

export function getDependentModules(module, programModules) {
  return programModules.filter(mod => mod.dependencies.includes(module.id));
}
